import React from 'react';
import { Box, Container, Typography, Grid, Paper, Button, Link, Avatar, Card, CardContent, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { API_BASE } from '../config';
import Features from './Features';
import CTA from './CTA';
import CaseStudiesCarousel from './CaseStudiesCarousel';
import HomeHero from './HomeHero';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/swiper-bundle.css';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import StarIcon from '@mui/icons-material/Star';
import ScienceIcon from '@mui/icons-material/Science';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CampaignIcon from '@mui/icons-material/Campaign';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StorefrontIcon from '@mui/icons-material/Storefront';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';

const services = [
  {
    icon: <ScienceIcon fontSize="large" color="primary" />, title: 'AI/ML Strategy & Consulting',
    desc: 'Have a project idea and need help implementing it? We’re here to consult you and share our knowledge to help you avoid all unnecessary pitfalls.'
  },
  {
    icon: <AssessmentIcon fontSize="large" color="warning" />, title: 'PoC of AI-Based Solution',
    desc: 'POC is an essential step before adopting any AI solution. If you have a project idea, our data science consultants will verify that your concept has potential.'
  },
  {
    icon: <StarIcon fontSize="large" color="success" />, title: 'MVP of AI-Based Product',
    desc: 'Need a breakthrough AI product? We’re ready to start with a version of just enough features to satisfy early customers and provide feedback for product development.'
  },
  {
    icon: <ModelTrainingIcon fontSize="large" color="info" />, title: 'Custom Model Development',
    desc: 'We can build and train custom models for your business needs, or retrain your existing ones (open-source and proprietary) for better efficiency and scalability.'
  },
  {
    icon: <DeveloperModeIcon fontSize="large" color="warning" />, title: 'AI Software Development',
    desc: 'If you need to develop an innovative web application from scratch, or empower the existing one with AI capabilities, let our experts help you.'
  },
  {
    icon: <PhoneAndroidIcon fontSize="large" color="primary" />, title: 'AI-Driven Mobile App Development',
    desc: 'Our experts are here to help you build your innovative mobile app from scratch and power it with AI capabilities that your users will love.'
  }
];

const stats = [
  { value: '83%', desc: 'of companies claim AI as a strategic business priority' },
  { value: '85%', desc: 'of customer service interactions were responded to by chatbots' },
  { value: '40%', desc: 'improved business efficiency with AI' }
];

const testimonials = [
  {
    name: 'Andrew Kovzel',
    role: 'CTO',
    company: 'Flo: Smart Period Tracker',
    rating: 5,
    text: "As a growing company, we found NehaInfoTech's expertise in data science invaluable. In almost two years of our cooperation, they've helped us define our data analytics strategy, build a scalable data pipeline, and improve predictions with a sophisticated neural network.",
    source: 'Clutch'
  },
  {
    name: 'Sarah Martinez',
    role: 'VP of Engineering',
    company: 'TechVision Inc',
    rating: 5,
    text: 'Working with NehaInfoTech transformed our approach to AI implementation. Their team delivered exceptional results on time and provided ongoing support that exceeded our expectations. Highly recommended!',
    source: 'Google Reviews'
  },
  {
    name: 'Michael Chen',
    role: 'Product Manager',
    company: 'DataFlow Solutions',
    rating: 5,
    text: 'The level of expertise and professionalism from NehaInfoTech is outstanding. They helped us navigate complex AI challenges and delivered a solution that significantly improved our operational efficiency.',
    source: 'LinkedIn'
  },
  {
    name: 'Emma Wilson',
    role: 'Chief Data Officer',
    company: 'InnovateCorp',
    rating: 5,
    text: 'NehaInfoTech has been instrumental in our digital transformation journey. Their innovative solutions and dedicated support have helped us stay ahead in a competitive market.',
    source: 'Clutch'
  }
];

const blogPosts = [
  {
    date: '22 NOVEMBER 2025',
    category: 'AI & Machine Learning',
    title: 'How Large Language Models Are Transforming Business Operations',
    excerpt: 'Explore how LLMs are revolutionizing customer service, content creation, and decision-making processes across industries.',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80&auto=format&fit=crop',
    readTime: '8 min read',
    author: 'Dr. Sarah Chen'
  },
  {
    date: '18 NOVEMBER 2025',
    category: 'Data Science',
    title: 'Building Production-Ready Machine Learning Pipelines',
    excerpt: 'A comprehensive guide to creating robust, scalable ML pipelines that transition seamlessly from development to production.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80&auto=format&fit=crop',
    readTime: '12 min read',
    author: 'Michael Rodriguez'
  },
  {
    date: '14 NOVEMBER 2025',
    category: 'Cloud Computing',
    title: 'Top Data Warehouse Vendors in 2025: A Comprehensive Review',
    excerpt: 'Compare leading cloud data warehouse solutions including Snowflake, BigQuery, and Redshift for your enterprise needs.',
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80&auto=format&fit=crop',
    readTime: '10 min read',
    author: 'Jennifer Lee'
  },
  {
    date: '10 NOVEMBER 2025',
    category: 'DevOps',
    title: 'DevOps as a Service: Your Game-Changer in Deployment',
    excerpt: 'Discover how DevOps as a Service can streamline your development workflow and accelerate time to market.',
    image: 'https://images.unsplash.com/photo-1618401479427-c8ef9465fbe1?w=800&q=80&auto=format&fit=crop',
    readTime: '7 min read',
    author: 'Alex Thompson'
  },
  {
    date: '7 NOVEMBER 2025',
    category: 'Business Intelligence',
    title: '10 Benefits of a Data Warehouse in Modern Business',
    excerpt: 'Learn how implementing a data warehouse can improve decision-making, reporting, and overall business intelligence.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80&auto=format&fit=crop',
    readTime: '6 min read',
    author: 'Emma Watson'
  },
  {
    date: '3 NOVEMBER 2025',
    category: 'AI Ethics',
    title: 'Responsible AI: Building Trust Through Transparency',
    excerpt: 'Understand the importance of ethical AI practices and how to implement responsible AI frameworks in your organization.',
    image: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800&q=80&auto=format&fit=crop',
    readTime: '9 min read',
    author: 'Dr. James Kumar'
  },
  {
    date: '29 OCTOBER 2025',
    category: 'MLOps',
    title: 'MLOps Best Practices for Enterprise Scale',
    excerpt: 'Master the art of deploying and maintaining machine learning models at scale with these proven MLOps strategies.',
    image: 'https://images.unsplash.com/photo-1639322537228-f710d846310a?w=800&q=80&auto=format&fit=crop',
    readTime: '11 min read',
    author: 'Rachel Adams'
  },
  {
    date: '25 OCTOBER 2025',
    category: 'Computer Vision',
    title: 'Real-Time Object Detection: Latest Advances in 2025',
    excerpt: 'Explore cutting-edge computer vision techniques and their applications in autonomous vehicles, security, and retail.',
    image: 'https://images.unsplash.com/photo-1535378917042-10a22c95931a?w=800&q=80&auto=format&fit=crop',
    readTime: '8 min read',
    author: 'Kevin Park'
  },
  {
    date: '20 OCTOBER 2025',
    category: 'Java & Spring Boot',
    title: 'Spring Boot 3.x: Building Enterprise Applications with Modern Java',
    excerpt: 'Explore the latest features in Spring Boot 3.x, including native compilation, observability improvements, and Jakarta EE 10 support for building high-performance enterprise applications.',
    image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=800&q=80&auto=format&fit=crop',
    readTime: '10 min read',
    author: 'Rajesh Sharma'
  },
  {
    date: '15 OCTOBER 2025',
    category: 'Microservices',
    title: 'Microservices Architecture with Spring Cloud: A Practical Guide',
    excerpt: 'Learn how to design, build, and deploy scalable microservices using Spring Cloud, including service discovery, API gateway, and distributed tracing.',
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80&auto=format&fit=crop',
    readTime: '12 min read',
    author: 'Priya Patel'
  },
  {
    date: '10 OCTOBER 2025',
    category: 'Jakarta EE',
    title: 'Jakarta EE 10: The Evolution of Enterprise Java',
    excerpt: 'Discover the new features and improvements in Jakarta EE 10, from updated persistence APIs to enhanced security and cloud-native capabilities.',
    image: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&q=80&auto=format&fit=crop',
    readTime: '9 min read',
    author: 'Vikram Singh'
  },
  {
    date: '5 OCTOBER 2025',
    category: 'Java Performance',
    title: 'Java Performance Optimization: JVM Tuning and Best Practices',
    excerpt: 'Master JVM tuning, garbage collection optimization, and performance profiling techniques to build high-throughput Java applications.',
    image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&q=80&auto=format&fit=crop',
    readTime: '11 min read',
    author: 'Amit Desai'
  },
  {
    date: '1 OCTOBER 2025',
    category: 'Oracle HCM',
    title: 'Oracle PeopleSoft HCM: Modernizing Human Capital Management',
    excerpt: 'Discover how Oracle PeopleSoft HCM streamlines HR processes, from talent acquisition to workforce analytics, with cloud-ready architecture and AI-powered insights.',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80&auto=format&fit=crop',
    readTime: '10 min read',
    author: 'Suresh Nair'
  },
  {
    date: '25 SEPTEMBER 2025',
    category: 'Retail Technology',
    title: 'Oracle Xstore & Xadmin: Next-Gen Retail Point of Service Solutions',
    excerpt: 'Learn how Oracle Xstore POS and Xadmin management tools deliver seamless omnichannel retail experiences with real-time inventory, mobile POS, and advanced analytics.',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80&auto=format&fit=crop',
    readTime: '9 min read',
    author: 'Neha Kapoor'
  },
  {
    date: '20 SEPTEMBER 2025',
    category: 'CRM Platforms',
    title: 'Salesforce CRM: Driving Customer Success with AI-Powered Insights',
    excerpt: 'Explore how Salesforce CRM transforms customer relationships through Einstein AI, comprehensive analytics, and seamless integration across sales, service, and marketing.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80&auto=format&fit=crop',
    readTime: '11 min read',
    author: 'Ananya Reddy'
  },
  {
    date: '15 SEPTEMBER 2025',
    category: 'BPM & CRM',
    title: 'Pega CRM: Intelligent Automation for Customer Engagement',
    excerpt: 'Understand how Pega CRM combines low-code development, AI-driven decisioning, and case management to deliver personalized customer experiences at scale.',
    image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80&auto=format&fit=crop',
    readTime: '10 min read',
    author: 'Karthik Iyer'
  },
  {
    date: '10 SEPTEMBER 2025',
    category: 'Digital Marketing',
    title: 'AI-Powered Digital Marketing: Transforming Customer Acquisition',
    excerpt: 'Discover how AI and machine learning are revolutionizing digital marketing strategies, from predictive analytics to personalized campaigns and automated optimization.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80&auto=format&fit=crop',
    readTime: '9 min read',
    author: 'Meera Krishnan'
  },
  {
    date: '5 SEPTEMBER 2025',
    category: 'Digital Marketing',
    title: 'SEO & Content Marketing Strategies for 2025: A Data-Driven Approach',
    excerpt: 'Learn advanced SEO techniques, content optimization strategies, and how to leverage data analytics to drive organic traffic and improve conversion rates.',
    image: 'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=800&q=80&auto=format&fit=crop',
    readTime: '8 min read',
    author: 'Aditya Verma'
  }
];

function BlogCarousel() {
  return (
    <Box sx={(theme) => ({ 
      position: 'relative',
      '& .swiper': {
        paddingBottom: '50px'
      },
      '& .swiper-button-prev, & .swiper-button-next': {
        width: 48,
        height: 48,
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        boxShadow: theme.shadows[3],
        '&:after': {
          fontSize: '20px',
          fontWeight: 'bold'
        },
        '&:hover': {
          backgroundColor: theme.palette.primary.dark
        }
      },
      '& .swiper-pagination-bullet': {
        width: 12,
        height: 12,
        backgroundColor: theme.palette.grey[400],
        opacity: 0.8
      },
      '& .swiper-pagination-bullet-active': {
        backgroundColor: theme.palette.primary.main,
        width: 24,
        borderRadius: 6
      }
    })}>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        slidesPerView={1}
        spaceBetween={30}
        navigation
        pagination={{ clickable: true }}
        autoplay={{ delay: 4500, disableOnInteraction: false }}
        breakpoints={{
          640: { slidesPerView: 1 },
          768: { slidesPerView: 2 },
          1024: { slidesPerView: 3 }
        }}
      >
        {blogPosts.map((post, idx) => (
          <SwiperSlide key={idx}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
            >
              <Box sx={{ position: 'relative', overflow: 'hidden', height: 200 }}>
                <Box
                  component="img"
                  src={post.image}
                  alt={post.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.5,
                    backdropFilter: 'blur(8px)'
                  }}
                >
                  <Chip 
                    label={post.category} 
                    size="small" 
                    color="primary"
                    sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                  />
                </Box>
              </Box>

              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {post.date}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    •
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {post.readTime}
                  </Typography>
                </Box>

                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    mb: 1.5,
                    lineHeight: 1.3,
                    minHeight: 64,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {post.title}
                </Typography>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 2,
                    lineHeight: 1.6,
                    minHeight: 48,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {post.excerpt}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    By {post.author}
                  </Typography>
                  <Button 
                    size="small" 
                    component={RouterLink}
                    to="/blog"
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Read More →
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
}

function TestimonialsCarousel() {
  return (
    <Box sx={(theme) => ({
      position: 'relative',
      maxWidth: 900,
      mx: 'auto',
      '& .swiper-button-prev, & .swiper-button-next': {
        zIndex: 6,
        width: 48,
        height: 48,
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        boxShadow: theme.shadows[3],
        '&:after': {
          fontSize: '20px'
        }
      },
      '& .swiper-pagination-bullet': {
        width: 12,
        height: 12,
        opacity: 0.8,
        background: theme.palette.grey[400]
      },
      '& .swiper-pagination-bullet-active': {
        background: theme.palette.primary.main,
        width: 24
      }
    })}>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        slidesPerView={1}
        spaceBetween={30}
        navigation
        pagination={{ clickable: true }}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        style={{ paddingBottom: 50 }}
      >
        {testimonials.map((testimonial, idx) => (
          <SwiperSlide key={idx}>
            <Paper
              elevation={3}
              sx={{
                p: 5,
                borderRadius: 3,
                position: 'relative',
                overflow: 'visible',
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -20,
                  left: 40,
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 3
                }}
              >
                <FormatQuoteIcon sx={{ color: 'white', fontSize: 32 }} />
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary', fontStyle: 'italic', mb: 3 }}>
                  "{testimonial.text}"
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    }}
                  >
                    {testimonial.name.charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {testimonial.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {testimonial.role}, {testimonial.company}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                    Reviewed on {testimonial.source}
                  </Typography>
                  <Typography sx={{ color: '#FFB400' }}>
                    {'★'.repeat(testimonial.rating)}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
}

export default function Home() {
  return (
    <Box>
      <HomeHero />
      {/* How We Can Help You - Modern UI/UX */}
      <Box sx={{ 
        py: 10,
        background: 'linear-gradient(180deg, #ffffff 0%, rgba(102, 126, 234, 0.03) 100%)'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            {/* Icon Badge */}
            <Box sx={{
              display: 'inline-block',
              mb: 3
            }}>
              <Box sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
              }}>
                <Typography sx={{ fontSize: '2.5rem' }}>💡</Typography>
              </Box>
            </Box>

            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 800, 
                mb: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              How We Can Help You
            </Typography>

            {/* Decorative Line */}
            <Box sx={{
              width: 100,
              height: 4,
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              mx: 'auto',
              mb: 3
            }} />

            <Typography 
              variant="h6"
              sx={{ 
                color: 'text.secondary',
                maxWidth: 900,
                mx: 'auto',
                lineHeight: 1.8,
                fontWeight: 400
              }}
            >
              We welcome opportunities to work alongside different teams over projects of any complexity. 
              By working together, we will develop new systems, solutions, and products to separate you from your competition.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {services.map((service, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 4, 
                    borderRadius: 3, 
                    minHeight: 280, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'flex-start',
                    background: 'white',
                    border: '1px solid',
                    borderColor: alpha('#667eea', 0.1),
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                      transform: 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.3s ease'
                    },
                    '&:hover': {
                      transform: 'translateY(-12px)',
                      boxShadow: '0 16px 48px rgba(102, 126, 234, 0.2)',
                      borderColor: alpha('#667eea', 0.3),
                      '&::before': {
                        transform: 'scaleX(1)'
                      },
                      '& .service-icon': {
                        transform: 'scale(1.1) rotate(5deg)',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }
                    }
                  }}
                >
                  {/* Icon Container */}
                  <Box 
                    className="service-icon"
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      transition: 'all 0.3s ease',
                      '& svg': {
                        fontSize: '2rem',
                        color: '#667eea'
                      }
                    }}
                  >
                    {service.icon}
                  </Box>

                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700,
                      mb: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    {service.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      lineHeight: 1.7,
                      flexGrow: 1
                    }}
                  >
                    {service.desc}
                  </Typography>

                  {/* Arrow indicator */}
                  <Box sx={{ 
                    mt: 2,
                    color: '#667eea',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.875rem'
                  }}>
                    Learn more →
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* CTA Button */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button 
              component={RouterLink} 
              to="/contact" 
              variant="contained"
              size="large"
              sx={{
                py: 2,
                px: 5,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Get Free Consultation →
            </Button>
          </Box>
        </Container>
      </Box>
      <Features />
      
      {/* Beneficial Results - Modern UI/UX */}
      <Box sx={{ 
        py: 10,
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%)',
        position: 'relative'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            {/* Icon Badge */}
            <Box sx={{
              display: 'inline-block',
              mb: 3
            }}>
              <Box sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
              }}>
                <Typography sx={{ fontSize: '2.5rem' }}>📊</Typography>
              </Box>
            </Box>

            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 800, 
                mb: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' }
              }}
            >
              Beneficial Results of Artificial Intelligence in Business
            </Typography>

            {/* Decorative Line */}
            <Box sx={{
              width: 100,
              height: 4,
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              mx: 'auto',
              mb: 3
            }} />

            <Typography 
              variant="h6"
              sx={{ 
                color: 'text.secondary',
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.8,
                fontWeight: 400
              }}
            >
              Measurable impact across every aspect of your business operations
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {stats.map((stat, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 5, 
                    borderRadius: 3, 
                    textAlign: 'center',
                    background: 'white',
                    border: '2px solid',
                    borderColor: alpha('#667eea', 0.1),
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 6,
                      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                      transform: 'scaleX(0)',
                      transformOrigin: 'center',
                      transition: 'transform 0.3s ease'
                    },
                    '&:hover': {
                      transform: 'translateY(-12px) scale(1.02)',
                      boxShadow: '0 20px 60px rgba(102, 126, 234, 0.25)',
                      borderColor: alpha('#667eea', 0.4),
                      '&::before': {
                        transform: 'scaleX(1)'
                      },
                      '& .stat-value': {
                        transform: 'scale(1.1)',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      },
                      '& .stat-icon': {
                        transform: 'rotate(360deg) scale(1.2)'
                      }
                    }
                  }}
                >
                  {/* Decorative Icon/Number Badge */}
                  <Box 
                    className="stat-icon"
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      color: '#667eea',
                      transition: 'all 0.5s ease'
                    }}
                  >
                    {idx + 1}
                  </Box>

                  <Typography 
                    className="stat-value"
                    variant="h2" 
                    sx={{ 
                      fontWeight: 800,
                      mb: 2,
                      color: '#667eea',
                      transition: 'all 0.3s ease',
                      fontSize: { xs: '2.5rem', md: '3rem' }
                    }}
                  >
                    {stat.value}
                  </Typography>
                  
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'text.secondary',
                      lineHeight: 1.7,
                      fontWeight: 500,
                      px: 2
                    }}
                  >
                    {stat.desc}
                  </Typography>

                  {/* Bottom accent */}
                  <Box sx={{
                    mt: 3,
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: alpha('#667eea', 0.1)
                  }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#667eea',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 1
                      }}
                    >
                      Proven Impact
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Additional CTA */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
              Ready to achieve similar results for your business?
            </Typography>
            <Button 
              component={RouterLink} 
              to="/portfolio"
              variant="outlined"
              size="large"
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                borderWidth: 2,
                borderColor: '#667eea',
                color: '#667eea',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  borderWidth: 2,
                  borderColor: '#667eea',
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              View Case Studies
            </Button>
          </Box>
        </Container>
      </Box>
      {/* Mission Section - Modern UI/UX */}
      <Box sx={{ 
        position: 'relative',
        py: 10,
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
        }
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            {/* Icon Badge */}
            <Box sx={{
              display: 'inline-block',
              mb: 3
            }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': {
                    transform: 'scale(1)',
                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
                  },
                  '50%': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)'
                  }
                }
              }}>
                <Typography sx={{ fontSize: '3rem' }}>🚀</Typography>
              </Box>
            </Box>

            {/* Main Heading */}
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 800, 
                mb: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                lineHeight: 1.2,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              Our Mission is to Bring the Power of AI to Every Business
            </Typography>

            {/* Decorative Line */}
            <Box sx={{
              width: 100,
              height: 4,
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              mx: 'auto',
              mb: 4
            }} />

            {/* Description */}
            <Typography 
              variant="h6"
              sx={{ 
                color: 'text.secondary',
                maxWidth: 900,
                mx: 'auto',
                lineHeight: 1.8,
                fontWeight: 400,
                mb: 2
              }}
            >
              We are a data science and analytics consulting firm delivering AI-powered solutions to companies 
              who want to leverage data and machine learning algorithms for business value.
            </Typography>
            <Typography 
              variant="body1"
              sx={{ 
                color: 'text.secondary',
                maxWidth: 900,
                mx: 'auto',
                lineHeight: 1.8,
                mb: 4
              }}
            >
              As an artificial intelligence company, NehaInfoTech is focused on AI and Big data software development. 
              We help businesses innovate with AI, enrich customer insights, automate processes & be more cost-efficient.
            </Typography>
          </Box>

          {/* Value Props Grid */}
          <Grid container spacing={3} sx={{ maxWidth: 1000, mx: 'auto' }}>
            {[
              { 
                icon: '🎯', 
                title: 'Data-Driven Insights', 
                desc: 'Transform raw data into actionable intelligence that drives strategic decisions'
              },
              { 
                icon: '🤖', 
                title: 'AI Innovation', 
                desc: 'Leverage cutting-edge machine learning and GenAI technologies for competitive advantage'
              },
              { 
                icon: '⚡', 
                title: 'Process Automation', 
                desc: 'Streamline operations and reduce costs through intelligent automation'
              },
              { 
                icon: '📈', 
                title: 'Business Growth', 
                desc: 'Scale efficiently with AI-powered solutions that adapt to your needs'
              }
            ].map((item, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Box sx={{
                  p: 3,
                  borderRadius: 3,
                  textAlign: 'center',
                  height: '100%',
                  background: 'white',
                  border: '1px solid',
                  borderColor: alpha('#667eea', 0.1),
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(102, 126, 234, 0.15)',
                    borderColor: alpha('#667eea', 0.3),
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%)'
                  }
                }}>
                  <Box sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2
                  }}>
                    <Typography sx={{ fontSize: '2rem' }}>{item.icon}</Typography>
                  </Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700, 
                      mb: 1,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      {/* Customer Success Stories - Modern UI/UX */}
      <Box sx={{ 
        py: 10,
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.02) 0%, rgba(118, 75, 162, 0.02) 100%)'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            {/* Icon Badge */}
            <Box sx={{
              display: 'inline-block',
              mb: 3
            }}>
              <Box sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
              }}>
                <Typography sx={{ fontSize: '2.5rem' }}>🎯</Typography>
              </Box>
            </Box>

            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 800, 
                mb: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              Our Customer Success Stories
            </Typography>

            {/* Decorative Line */}
            <Box sx={{
              width: 100,
              height: 4,
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              mx: 'auto',
              mb: 3
            }} />

            <Typography 
              variant="h6"
              sx={{ 
                color: 'text.secondary',
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.8,
                fontWeight: 400
              }}
            >
              Real results from real clients. Discover how we've helped businesses transform 
              their operations and achieve measurable success with AI and data science.
            </Typography>
          </Box>

          <CaseStudiesCarousel />

          {/* CTA */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button 
              component={RouterLink} 
              to="/portfolio"
              variant="contained"
              size="large"
              sx={{
                py: 2,
                px: 5,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              View All Success Stories →
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section - Modern UI/UX */}
      <Box sx={{ 
        py: 10,
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.04) 0%, rgba(118, 75, 162, 0.04) 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
        }
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            {/* Icon Badge */}
            <Box sx={{
              display: 'inline-block',
              mb: 3
            }}>
              <Box sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
              }}>
                <Typography sx={{ fontSize: '2.5rem' }}>💬</Typography>
              </Box>
            </Box>

            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 800, 
                mb: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              Let Our Clients Do the Talking
            </Typography>

            {/* Decorative Line */}
            <Box sx={{
              width: 100,
              height: 4,
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              mx: 'auto',
              mb: 3
            }} />

            <Typography 
              variant="h6"
              sx={{ 
                color: 'text.secondary',
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.8,
                fontWeight: 400
              }}
            >
              Don't just take our word for it. Hear directly from the clients who have experienced 
              the transformative impact of our AI and data science solutions.
            </Typography>
          </Box>

          <TestimonialsCarousel />

          {/* Trust Indicators */}
          <Box sx={{ 
            mt: 6, 
            textAlign: 'center',
            p: 4,
            borderRadius: 3,
            background: 'white',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)'
          }}>
            <Grid container spacing={4} alignItems="center" justifyContent="center">
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1
                    }}
                  >
                    98%
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Client Satisfaction
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1
                    }}
                  >
                    50+
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Projects Delivered
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1
                    }}
                  >
                    5★
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Average Rating
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Industries Section - Modern UI/UX */}
      <Box sx={{ 
        py: 10,
        background: 'linear-gradient(180deg, #ffffff 0%, rgba(102, 126, 234, 0.03) 100%)'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            {/* Icon Badge */}
            <Box sx={{
              display: 'inline-block',
              mb: 3
            }}>
              <Box sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
              }}>
                <Typography sx={{ fontSize: '2.5rem' }}>🏭</Typography>
              </Box>
            </Box>

            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 800, 
                mb: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              Industries We Are Transforming
            </Typography>

            {/* Decorative Line */}
            <Box sx={{
              width: 100,
              height: 4,
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              mx: 'auto',
              mb: 3
            }} />

            <Typography 
              variant="h6"
              sx={{ 
                color: 'text.secondary',
                maxWidth: 900,
                mx: 'auto',
                lineHeight: 1.8,
                fontWeight: 400
              }}
            >
              We deliver tailored AI and software solutions across industries — from marketing to manufacturing.
              Our expertise spans multiple sectors, driving innovation and measurable business outcomes.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {[
              { title: 'Advertising', icon: <CampaignIcon fontSize="large" />, desc: 'Improve advertising with AI-driven customer insights and targeting.', link: '/industries/digital-marketing' },
              { title: 'FinTech', icon: <AccountBalanceIcon fontSize="large" />, desc: 'Optimize processes, reduce fraud and automate workflows.', link: '/industries/finance' },
              { title: 'Retail & E‑commerce', icon: <StorefrontIcon fontSize="large" />, desc: 'Personalization, recommendations and demand forecasting.', link: '/industries/retail' },
              { title: 'Healthcare', icon: <LocalHospitalIcon fontSize="large" />, desc: 'Improve patient outcomes, streamline workflows and extract value from healthcare data.', link: '/industries/healthcare' },
              { title: 'Logistics & Supply Chain', icon: <LocalShippingIcon fontSize="large" />, desc: 'Optimize routes, inventory and warehouse operations.', link: '/industries/manufacturing' },
              { title: 'Your Industry', icon: <BusinessCenterIcon fontSize="large" />, desc: 'Contact us to explore how AI can transform your business.', link: '/contact', special: true }
            ].map((it) => (
              <Grid item xs={12} sm={12} md={6} key={it.title}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    minHeight: 180,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 3,
                    alignItems: 'stretch',
                    background: 'white',
                    border: '1px solid',
                    borderColor: alpha('#667eea', 0.1),
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: 6,
                      height: '100%',
                      background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                      transform: 'scaleY(0)',
                      transformOrigin: 'bottom',
                      transition: 'transform 0.3s ease'
                    },
                    '&:hover': { 
                      transform: 'translateY(-8px)', 
                      boxShadow: '0 16px 48px rgba(102, 126, 234, 0.2)',
                      borderColor: alpha('#667eea', 0.3),
                      '&::before': {
                        transform: 'scaleY(1)'
                      },
                      '& .industry-icon-box': {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        transform: 'scale(1.05) rotate(-5deg)'
                      }
                    }
                  }}
                >
                  {/* Icon Box */}
                  <Box 
                    className="industry-icon-box"
                    sx={{ 
                      width: { xs: '100%', sm: 120 }, 
                      minHeight: { xs: 120, sm: '100%' },
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '& svg': {
                        fontSize: '3rem',
                        color: '#667eea'
                      }
                    }}
                  >
                    {it.icon}
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: 700,
                          mb: 1.5,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}
                      >
                        {it.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.secondary',
                          lineHeight: 1.7
                        }}
                      >
                        {it.desc}
                      </Typography>
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button 
                        component={RouterLink} 
                        to={it.link}
                        size="small"
                        endIcon={<span>→</span>}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          color: '#667eea',
                          '&:hover': {
                            background: alpha('#667eea', 0.08)
                          }
                        }}
                      >
                        {it.special ? 'Contact us' : 'Learn more'}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Blog Section - Modern UI/UX */}
      <Box sx={{ 
        py: 10,
        background: 'linear-gradient(180deg, rgba(102, 126, 234, 0.02) 0%, #ffffff 100%)',
        position: 'relative'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            {/* Icon Badge */}
            <Box sx={{
              display: 'inline-block',
              mb: 3
            }}>
              <Box sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                animation: 'float 3s ease-in-out infinite',
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-10px)' }
                }
              }}>
                <Typography sx={{ fontSize: '2.5rem' }}>📚</Typography>
              </Box>
            </Box>

            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 800, 
                mb: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              Latest Insights & Articles
            </Typography>

            {/* Decorative Line */}
            <Box sx={{
              width: 100,
              height: 4,
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              mx: 'auto',
              mb: 3
            }} />

            <Typography 
              variant="h6"
              sx={{ 
                color: 'text.secondary',
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.8,
                fontWeight: 400
              }}
            >
              Explore insights, trends, and best practices in AI, data science, and software engineering.
              Stay ahead with expert knowledge from our team.
            </Typography>
          </Box>

          {/* Blog Carousel with enhanced wrapper */}
          <Box sx={{
            position: 'relative',
            '& .swiper': {
              pb: 5
            }
          }}>
            <BlogCarousel />
          </Box>

          {/* CTA Button */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button 
              variant="contained"
              size="large"
              component={RouterLink}
              to="/blog"
              endIcon={<span>→</span>}
              sx={{ 
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 3,
                px: 5,
                py: 1.8,
                fontSize: '1.1rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.35)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 32px rgba(102, 126, 234, 0.45)',
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                }
              }}
            >
              View All Articles
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Box 
        sx={{ 
          py: 8,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
          }
        }}
      >
        <Container>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography 
              variant="h3" 
              gutterBottom
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              Get Started with Data Science Today
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto', mb: 1 }}
            >
              Let's Talk About Your Project
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ maxWidth: 700, mx: 'auto' }}
            >
              Our team of experts is ready to help you transform your data into actionable insights. 
              Schedule a consultation to discuss your goals and discover how we can drive your success.
            </Typography>
          </Box>
          <ContactForm />
        </Container>
      </Box>

      {/* Footer-like columns on Home page (About Us + services) */}
      <Box sx={{ bgcolor: '#0f2633', color: 'white', py: 6, mt: 8 }} component="section">
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h5" sx={{ mb: 2 }}>About Us</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link component={RouterLink} to="/portfolio" color="inherit" underline="hover">Success Stories</Link>
                <Link component={RouterLink} to="/blog" color="inherit" underline="hover">Blog</Link>
                <Link component={RouterLink} to="/careers" color="inherit" underline="hover">Careers</Link>
                <Link component={RouterLink} to="/about" color="inherit" underline="hover">About</Link>
                <Link component={RouterLink} to="/contact" color="inherit" underline="hover">Write for Us</Link>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Link href="mailto:info@nehainfotech.com" color="inherit" underline="hover">info@nehainfotech.com</Link>
                <Typography sx={{ mt: 1 }}>+1 305 447 7330</Typography>
                <Typography sx={{ mt: 1 }}>333 S.E. 2nd Avenue, Suite 2000, Miami, Florida, 33131, USA</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ mb: 2 }}>Expertise & Services</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography>Data Science & AI</Typography>
                <Typography>Machine Learning</Typography>
                <Typography>AI Software Development</Typography>
                <Typography>AI Mobile Apps Development</Typography>
                <Typography>Natural Language Processing</Typography>
                <Typography>Data Capture & OCR</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ mb: 2 }}>Big Data Solutions</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography>Big Data Development</Typography>
                <Typography>Data Architecture Engineering</Typography>
                <Typography>Data Warehouse Engineering</Typography>
                <Typography>BI & Data Visualizations</Typography>
                <Typography>Predictive Analytics</Typography>
                <Typography>Cloud Services</Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

  function ContactForm() {
    const [form, setForm] = React.useState({ name: '', company: '', email: '', phone: '', message: '' });
    const [status, setStatus] = React.useState('idle');
    const [errors, setErrors] = React.useState({});

    const validateField = (name, value) => {
      if (name === 'name' || name === 'company' || name === 'message') {
        if (!value || !value.trim()) return 'This field is required.';
      }
      if (name === 'email') {
        if (!value || !value.trim()) return 'Email is required.';
        // simple email check
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!re.test(value)) return 'Enter a valid email address.';
      }
      return '';
    };

    const handleChange = (e) => {
      const { name, value } = e.target;
      setForm(prev => ({ ...prev, [name]: value }));
      setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    };

    const validateAll = () => {
      const next = {};
      ['name', 'company', 'email', 'message'].forEach(f => {
        next[f] = validateField(f, form[f]);
      });
      setErrors(next);
      // form valid if all messages are empty
      return Object.values(next).every(v => !v);
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!validateAll()) {
        return;
      }
      setStatus('sending');
      try {
        const res = await fetch(`${API_BASE}/api/contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form)
        });
        if (res.ok) {
          setStatus('sent');
          setForm({ name: '', company: '', email: '', phone: '', message: '' });
          setErrors({});
        } else {
          setStatus('error');
        }
      } catch (err) {
        console.error(err);
        setStatus('error');
      }
    };

    const isFormValid = React.useMemo(() => {
      // required fields: name, company, email, message
      if (!form.name.trim() || !form.company.trim() || !form.email.trim() || !form.message.trim()) return false;
      if (errors.name || errors.company || errors.email || errors.message) return false;
      return true;
    }, [form, errors]);

    return (
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4, maxWidth: 600, mx: 'auto' }} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <input name="name" value={form.name} onChange={handleChange} type="text" placeholder="Name*" style={{ width: '100%', padding: 12, borderRadius: 8, border: errors.name ? '1px solid #d32f2f' : '1px solid #ccc' }} />
            {errors.name && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{errors.name}</Typography>}
          </Grid>
          <Grid item xs={12} sm={6}>
            <input name="company" value={form.company} onChange={handleChange} type="text" placeholder="Company Name*" style={{ width: '100%', padding: 12, borderRadius: 8, border: errors.company ? '1px solid #d32f2f' : '1px solid #ccc' }} />
            {errors.company && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{errors.company}</Typography>}
          </Grid>
          <Grid item xs={12} sm={6}>
            <input name="email" value={form.email} onChange={handleChange} type="email" placeholder="Business email*" style={{ width: '100%', padding: 12, borderRadius: 8, border: errors.email ? '1px solid #d32f2f' : '1px solid #ccc' }} />
            {errors.email && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{errors.email}</Typography>}
          </Grid>
          <Grid item xs={12} sm={6}>
            <input name="phone" value={form.phone} onChange={handleChange} type="text" placeholder="Phone" style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #ccc' }} />
          </Grid>
          <Grid item xs={12}>
            <textarea name="message" value={form.message} onChange={handleChange} placeholder="How can we help you?" style={{ width: '100%', padding: 12, borderRadius: 8, border: errors.message ? '1px solid #d32f2f' : '1px solid #ccc', minHeight: 100 }} />
            {errors.message && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{errors.message}</Typography>}
          </Grid>
        </Grid>
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button variant="contained" size="large" type="submit" disabled={status === 'sending' || !isFormValid}>Send Message</Button>
        </Box>
        <Typography align="center" color="text.secondary" sx={{ mt: 2, fontSize: 12 }}>
          By clicking Send Message, you agree to our Terms of Use and Privacy Policy.
        </Typography>
        {status === 'sent' && <Typography align="center" color="success.main" sx={{ mt: 2 }}>Thanks — your message was sent.</Typography>}
        {status === 'error' && <Typography align="center" color="error.main" sx={{ mt: 2 }}>Something went wrong. Please try again later.</Typography>}
      </Box>
    );
  }
